import { client } from "@/client";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { CustomToastComponent } from "@/components/shared";
import { useMutation } from "@apollo/client/react";
import {
  AddPermanentServiceMutation,
  AddPermanentServiceMutationVariables,
  Service,
  AddPermanentServiceDocument,
  FetchTenderByIdDocument,
  FetchServicesOnTenderDocument,
  Pricing,
} from "@/graphql/__generated__/graphql";

/**
 * Custom hook for adding a service to a tender and handling the necessary cache updates.
 * @param {Object} options - Optional configuration options
 * @param {boolean} [options.disableRefetch=false] - Whether to disable refetching queries after mutation
 * @param {boolean} [options.disableToast=false] - Whether to disable showing success toast
 * @returns {Object} - Contains the addPermanentServiceMutation function, loading state, and error state.
 */
export default function useAddPermanentServiceMutationHook(options?: { disableRefetch?: boolean, disableToast?: boolean }): { addPermanentServiceMutation: any, loading: any, error: any } {
  // Hooks
  const { t } = useTranslation(["toasts"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  const [addPermanentServiceMutation, { loading, error }] = useMutation<AddPermanentServiceMutation, AddPermanentServiceMutationVariables>(
    AddPermanentServiceDocument, {
      onCompleted(data) {
        if (!options?.disableToast) {
          toast(CustomToastComponent, {
            toastId: "permanent-service-add-success",
            data: {
              title: t("toasts.permanentserviceaddedsuccessfully"),
              type: "success"
            }
          });
        }

        updateCache({
          ...data.addPermanentService,
          costDetails: [],
          cars: [],
          purchases: [],
          pricing: {
            totalRevenue: 0,
            totalMargin: 0,
            totalEmployeeCount: 0,
            reserveEmployeeCount: 0,
            necessaryEmployeeCount: 0,
            monthlyRate: 0,
            yearlyRate: 0,
            totalSurcharge: 0,
            totalPricePerHour: 0,
            hasCustomSchedule: false,
            servicePricing: [],
            carPurchasePricing: []
          } as Pricing
        });
      },
      refetchQueries: options?.disableRefetch
        ? []
        : [
          {
            query: FetchTenderByIdDocument,
            variables: { id: Number(tenderID) }
          },
          {
            query: FetchServicesOnTenderDocument,
            variables: { tenderId: Number(tenderID) }
          }
        ]
    });

  /**
   * Adds reference of new Service to the Tender's services array.
   * @param {Service} newData - The new cost details to be added.
   */
  const updateCache = (newData: Service) => {
    const tenderId = newData.tenderId;
    // Get the cache reference of the new Service
    const newServiceReference = client.cache.identify({
      __typename: "PermanentService",
      id: newData.id
    });

    // Update the `services` field of the Tender object
    client.cache.modify({
      id: client.cache.identify({ __typename: "Tender", id: tenderId }),
      fields: {
        services(existingServices = []) {
          if (!existingServices) {
            return [newServiceReference];
          }

          return [...existingServices, { __ref: newServiceReference }];
        }
      }
    });
  };

  return {
    addPermanentServiceMutation,
    loading,
    error
  };
}
